import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionsTable = ({ sessions, loading, onLogoutSession, onRefresh }) => {
  const getDeviceIcon = (deviceType) => {
    const icons = {
      desktop: 'Monitor',
      mobile: 'Smartphone',
      tablet: 'Tablet',
      unknown: 'HelpCircle'
    };
    return icons?.[deviceType] || 'HelpCircle';
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
          <Icon name="CheckCircle2" size={12} />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        <Icon name="XCircle" size={12} />
        Inactive
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={48} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (sessions?.length === 0) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Icon name="Monitor" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium text-foreground mb-2">No sessions found</p>
          <p className="text-muted-foreground mb-4">Try adjusting your filters or refresh the page</p>
          <Button variant="outline" iconName="RefreshCw" onClick={onRefresh}>
            Refresh Sessions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-foreground">Active Sessions</h3>
        <span className="text-sm text-muted-foreground">{sessions?.length} total</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Device</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">IP Address</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Location</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Login Time</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Activity</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions?.map((session) => (
              <tr key={session?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon name={getDeviceIcon(session?.device_type)} size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {session?.device_name || session?.device_type?.charAt(0)?.toUpperCase() + session?.device_type?.slice(1)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {session?.browser_name} • {session?.os_name}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm text-foreground font-mono">{session?.ip_address || 'Unknown'}</p>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <p className="text-sm text-foreground">{session?.location || 'Unknown'}</p>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm text-foreground">{formatDateTime(session?.login_time)}</p>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm text-foreground">{getTimeSince(session?.last_activity)}</p>
                </td>
                <td className="py-4 px-4">
                  {getStatusBadge(session?.is_active)}
                </td>
                <td className="py-4 px-4 text-right">
                  {session?.is_active ? (
                    <Button
                      variant="danger"
                      size="sm"
                      iconName="LogOut"
                      onClick={() => onLogoutSession(session)}
                    >
                      Logout
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {session?.logout_reason || 'Logged out'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SessionsTable;